import React, { forwardRef, Fragment, useState, useEffect } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { customValidators, object, string, date } from '../../Helpers/Validators/customValidators';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { sub } from 'date-fns';
import States from '../../Helpers/Lists/States';
import StatesWithDisabled from '../../Helpers/Lists/StatesWithDisabled';
import GetHealthClassFromBmi from '../Digital/Helpers/GetHealthClassFromBmi';
import { InputMaskHeight, InputMaskWeight, InputMaskDate } from '../../Helpers/Formatters';
import { CheckboxInput, TextInput, SelectInput, StyledRadioInput } from '../../Helpers/Inputs';
import TobaccoUse from '../../Helpers/Lists/TobaccoUse';
import BackNext from '../Common/SubComponents/BackNext';
import { emptyStringToUndefined } from '../../Helpers/Formatters/EmptyStringToUndefined';
import { getBasicInfo, setBasicInfo } from '../../../sessionStorage';
import { calculateBMI } from '../../Helpers/Conversion';
import MaleIcon from '../../../icons/male.svg';
import FemaleIcon from '../../../icons/female.svg';
import { createTheme, ThemeProvider } from '@material-ui/core';
import { calculateExactAge, calculateNearestAge } from '../Common/Helpers/AgeCalculation';
import ModalWarning from '../../Helpers/Widgets/ModalWarning';

import './BasicInfo.scss';

const BasicInfo = forwardRef((props, ref) => {
  const theme = createTheme({
    palette: {
      primary: {
        main: `${props.brandTheme ? props.brandTheme : "#3f51b5"}`,
      },
    },
    typography: {
      fontFamily: "museo-sans",
      body1: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    overrides: {
      MuiInputBase: {
        input: {
          fontSize: 16,
        },
        root: {
          height: 56,
        },
      },
      MuiFilledInput: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.05)",
        },
        underline: {
          "&::before": {
            borderBottom: "solid 2px #b2b2b2",
          },
          "&:hover::before": {
            borderBottomWidth: "2px",
          },
        },
      },
      MuiFormLabel: {
        root: {
          fontSize: 16,
          color: "rgba(0,0,0,0.6)",
        },
      },
      MuiFormHelperText: {
        root: {
          marginTop: 0,
          paddingTop: 0,
        },
      },
      MuiInputLabel: {
        shrink: {
          color: "rgba(0,0,0,0.3) !important",
        },
      },
      MuiSelect: {
        fontSize: 16,
        icon: {
          color: "rgba(0,0,0,1)",
        },
        iconFilled: {
          right: 16,
        },
      },
    },
  });

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: BasicInfo.ValidationSchema(props.ageSpecs),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  
  const [purlMismatch, setPurlMismatch] = useState(false);
  const [initialBasicInfo] = useState(props.formState);
  
  useEffect(() => {
    (async () => {
      if (props.isPurlVerification && props.selectedQuote && !initialBasicInfo.birthdate) {
        let mismatch = false;
        if (props.formState.birthdate !== '' && await triggerValidation('birthdate')) {
          let initialAge, newAge;
          if (props.selectedQuote.ageDetermination && props.selectedQuote.ageDetermination.toLowerCase() === 'nearest') {
            initialAge = calculateNearestAge(new Date(initialBasicInfo.fakeBirthdate));
            newAge = calculateNearestAge(new Date(props.formState.birthdate));
          } else {
            initialAge = calculateExactAge(new Date(initialBasicInfo.fakeBirthdate));
            newAge = calculateExactAge(new Date(props.formState.birthdate));
          }
          mismatch = mismatch || initialAge !== newAge;
        }
        mismatch = mismatch || initialBasicInfo.gender !== props.formState.gender;
        mismatch = mismatch || initialBasicInfo.state !== props.formState.state;
        mismatch = mismatch || initialBasicInfo.tobacco !== props.formState.tobacco;
        setPurlMismatch(mismatch);
      }
    })();
  }, [props.formState]);

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    if(field === 'gender' || field === 'birthdate' || field === 'tobacco'){
      triggerValidation(field);
    }
  };

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'state' });
    setValue('state', props.formState.state);
    register({ name: 'gender' });
    setValue('gender', props.formState.gender);
    register({ name: 'birthdate' });
    setValue('birthdate', props.formState.birthdate);
    register({ name: 'height' });
    setValue('height', props.formState.height);
    register({ name: 'healthClass' });
    setValue('healthClass', props.formState.healthClass);
    register({ name: 'weight' });
    setValue('weight', props.formState.weight);
    register({ name: 'usResident' });
    setValue('usResident', props.formState.usResident);
    register({ name: 'isJuvenile' });
    setValue('isJuvenile', props.juviMode);
    register({ name: 'bmi' });
    setValue('bmi', props.formState.bmi);
    register({ name: 'tobacco' });
    setValue('tobacco', props.formState.tobacco);
  }, [register]);

  function calculateTotalHeight() {
    const radix = 10;
    const heightArray = props.formState.height.split(" ");
    const heightFeet = parseInt(heightArray[0], radix);
    let heightInches;
    for (let i = 1; i < heightArray.length; i++) {
      if (heightArray[i] !== "" && heightArray[i] !== "ft") {
        heightInches = parseInt(heightArray[i], radix);
        break;
      }
    };
    return heightFeet * 12 + heightInches;
  }

  useEffect(() => {
    if (props.formState.height && props.formState.weight) {
      const weight = props.formState.weight.split(" ")[0];
      const totalHeightInches = calculateTotalHeight();
      const calculatedBMI = calculateBMI(totalHeightInches, weight);
      const healthClassFromBmi = GetHealthClassFromBmi(calculatedBMI);
      handleValueChange("bmi", calculatedBMI);
      handleValueChange("healthClass", healthClassFromBmi);

    }
  }, [props.formState.height, props.formState.weight]);

  const onSubmit = () => {
    
    const totalHeight = calculateTotalHeight();
    const weight = props.formState.weight.split(" ")[0];

    const basicInfoFromSessionStorage = getBasicInfo();
    basicInfoFromSessionStorage.gender = props.formState.gender;
    basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
    basicInfoFromSessionStorage.state = props.formState.state;
    basicInfoFromSessionStorage.height = props.formState.height;
    basicInfoFromSessionStorage.weight = props.formState.weight;
    basicInfoFromSessionStorage.healthClass = props.formState.healthClass;
    if (props.tobaccoToTrueFalseString != null) {
      basicInfoFromSessionStorage.tobacco = props.tobaccoToTrueFalseString(props.formState.tobacco);
    } else {
      basicInfoFromSessionStorage.tobacco = props.formState.tobacco;
    }
    basicInfoFromSessionStorage.usResident = props.formState.usResident;
    basicInfoFromSessionStorage.bmi = props.formState.bmi;
    setBasicInfo(basicInfoFromSessionStorage);

    if (props.validateBMI != null && props.validateBMI(totalHeight, props.formState.bmi, weight) && props.onDeclined != null) {
      props.onDeclined();
    } else {
      if (props.openEditPopUp) {
        props.setOpenEditPopUp(false, purlMismatch);
      } else {
        props.onNext();
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="basic-info d-flex justify-content-center">
        <Form onSubmit={handleSubmit(onSubmit)} ref={ref}>
          <p className="iam mx-auto">{props.juviMode ? "My Child is..." : "I am..."}</p>

          <StyledRadioInput
            name="gender"
            id="gender"
            value={props.formState.gender}
            options={[{ value: "F", label: "Female", icon: <FemaleIcon /> }, { value: "M", label: "Male", icon: <MaleIcon /> }]}
            onChange={e => handleValueChange('gender', e)}
            errors={errors.gender}
          />

          <Form.Group>
            <TextInput
              name="birthdate"
              id="birthdate"
              label="Date of Birth"
              value={props.formState.birthdate}
              errors={errors && errors.birthdate || props.isPurlVerification && !props.formState.birthdate}
              inputRef={register}
              onChange={(e) => handleValueChange('birthdate', e.target.value)}
              InputProps={{
                inputComponent: InputMaskDate,
              }}
              className="basicInfo-date"
            />
          </Form.Group>

          <Form.Group>
            <SelectInput
              name="state"
              id="state"
              value={props.formState.state}
              onChange={(e) => handleValueChange('state', e.target.value)}
              label="State"
              inputRef={register}
              selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
              errors={errors && errors.state}
              aria-invalid="true"
              aria-describedby="state"
            />
          </Form.Group>

          {!props.juviMode &&
            (props.isTobaccoVerbiage ?
              <Form.Group className='mb-1'>
                <StyledRadioInput
                  name="tobacco"
                  id="tobacco"
                  label="Have you used any form of tobacco or nicotine in the last 12 months?"
                  value={props.formState.tobacco}
                  options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                  onChange={e => handleValueChange('tobacco', e)}
                  errors={errors.tobacco}
                  isBlankIcon={true}
                  isBlankCheckMark={false}
                />
              </Form.Group>
              :
            <Fragment>
              <Form.Group>
                <SelectInput
                  name="tobacco"
                  id="tobacco"
                  value={props.formState.tobacco}
                  onChange={(e) => handleValueChange('tobacco', e.target.value)}
                  label="Tobacco/Nicotine Use"
                  inputRef={register}
                  selectOptions={props.tobaccoOptions || TobaccoUse()}
                  errors={errors && errors.tobacco}
                  aria-invalid="true"
                  aria-describedby="state"
                />
              </Form.Group>
            </Fragment>)
          }

          <Row>
            <Col className="ppr-10_5">
              <Form.Group>
                <TextInput
                  name="height"
                  id="height"
                  label="Height"
                  value={props.formState.height}
                  errors={errors && errors.height || props.isPurlVerification && !props.formState.height}
                  inputRef={register}
                  onChange={(e) => handleValueChange('height', e.target.value)}
                  InputProps={{
                    inputComponent: InputMaskHeight,
                  }}
                  className="basicInfo-height"
                />
              </Form.Group>
            </Col>

            <Col className="ppl-10_5">
              <Form.Group>
                <TextInput
                  name="weight"
                  id="weight"
                  label="Weight"
                  value={props.formState.weight}
                  errors={errors && errors.weight || props.isPurlVerification && !props.formState.weight}
                  inputRef={register}
                  onChange={(e) => handleValueChange('weight', e.target.value)}
                  InputProps={{
                    inputComponent: InputMaskWeight,
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {props.showPolicyReplacementOption && (
            <Form.Group className="m-0">
              <CheckboxInput
                name="replacePolicy"
                id="replacePolicy"
                checked={props.formState.replacePolicy}
                onChange={(e) => props.onFieldChange('replacePolicy', e.target.checked)}
                errors={errors.replacePolicy}
                inputRef={register}
                label="I am replacing a life insurance policy"
                color="primary"
              />
            </Form.Group>
          )}

          {purlMismatch && 
          <Col xs={12}>
            <ModalWarning message='Your quotes have been refreshed to reflect the information you provided. Take a moment to review the updated results and choose the product that best aligns with your needs.' />
          </Col>
          }

          <BackNext
            hideBack={props.hideBack}
            onBack={props.onBack}
            hide={props.hide}
            continueLabel={props.continueLabel || { text: (props.isPurlVerification && purlMismatch ? 'Back to Results' : 'Continue') }}
          />
        </Form>
      </div>
    </ThemeProvider>
  );
});

BasicInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  hideBack: PropTypes.bool,
  ageSpecs: PropTypes.object.isRequired,
  tobaccoOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.any.isRequired,
  })),
  tobaccoToTrueFalseString: PropTypes.func,
  onBack: PropTypes.func,
  brandTheme: PropTypes.string,
  validateBMI: PropTypes.func,
  openEditPopUp: PropTypes.bool,
  setOpenEditPopUp: PropTypes.func,
  isPurlVerification: PropTypes.bool,
  onDeclined: PropTypes.func,
  unavailableStates: PropTypes.array,
  isTobaccoVerbiage: PropTypes.bool,
  hide: PropTypes.func,
  continueLabel: PropTypes.string,
};

BasicInfo.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }), `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays } days` : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    height: string().when('isJuvenile', {
      is: true,
      then: customValidators.string()
        .required('Height is required.')
        .isValidHeightString('Invalid height.', 0),
      otherwise: customValidators.string()
        .required('Height is required.')
        .isValidHeightString('Invalid height.', 3),
    }),
    weight: string().when('isJuvenile', {
      is: true,
      then: customValidators.string()
        .required('Weight is required.'),
      otherwise: customValidators.string()
        .required('Weight is required.')
        .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395),
    }),
    tobacco: string().transform(emptyStringToUndefined).when('isJuvenile', {
      is: true,
      then: string().notRequired(),
      otherwise: string()
        .required('This field is required.'),
    }),
  });
};

BasicInfo.DefaultFormState = (prefill = false, basicInfo = null, juviMode = false, mctQuoterData = null, isActivePurl = false) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    fakeBirthdate: prefill ? '' : (basicInfo && basicInfo.fakeBirthdate) || '',
    tobacco: (prefill || isActivePurl) ? basicInfo.tobacco === 'false' ? 'false' : 'Never' : (mctQuoterData && mctQuoterData.isFromAmerico ? mctQuoterData.tobacco : (mctQuoterData ? mctQuoterData.tobacco ==='Never'? 'false' : 'true':'')) || '',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    height: prefill ? '5 ft 10 in' : (basicInfo && basicInfo.height) || '',
    weight: prefill ? '175 pounds' : (basicInfo && basicInfo.weight) || '',
    healthClass: prefill ? 'P' : (basicInfo && basicInfo.healthClass) || '',
    usResident: true,
    replacePolicy: prefill ? false : (basicInfo && basicInfo.replacePolicy) || false,
    preQuoteHealthClass: (basicInfo && basicInfo.preQuoteHealthClass) || '',
    preQuoteAbbreviatedQualified: (basicInfo && basicInfo.preQuoteAbbreviatedQualified) || '',
    bmi: (basicInfo && basicInfo.bmi) || undefined,
    firstName: (basicInfo && basicInfo.firstName) || '',
    lastName: (basicInfo && basicInfo.lastName) || '',
    phone: (basicInfo && basicInfo.phone) || '',
  };
};

export default BasicInfo;



// WEBPACK FOOTER //
// ./src/components/Quoters/Common/BasicInfo.js